
import api from "../../services/api"; // Ensure the API instance is correctly imported
import { CashFlow, CashFlowsResponse } from "../../types";

interface FetchCashFlowsParams {
  estateId: string;
  page?: number;
  limit?: number;
}

export class CashFlowService {
  /**
   * Fetch paginated cash flows.
   * @param estateId - UUID of the estate.
   * @param page - Current page number.
   * @param limit - Number of cash flows per page.
   * @returns A promise that resolves to CashFlowsResponse.
   */
  static async fetchCashFlows({ estateId, page = 1, limit = 10 }: FetchCashFlowsParams): Promise<CashFlowsResponse> {
    try {
      const response = await api.get<CashFlowsResponse>(`/cashflows/${estateId}`, {
        params: { page, limit },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching cash flows:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch cash flows.');
    }
  }

  /**
   * Fetch a single cash flow by ID.
   * @param estateId - UUID of the estate.
   * @param cashFlowId - ID of the cash flow.
   * @returns A promise that resolves to a CashFlow.
   */
  static async fetchCashFlowById(estateId: string, cashFlowId: string): Promise<CashFlow> {
    try {
      const response = await api.get<CashFlow>(`/cashflows/${estateId}/${cashFlowId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching cash flow:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch cash flow.');
    }
  }

  /**
   * Create a new cash flow.
   * @param estateId - UUID of the estate.
   * @param cashFlowData - Data for the new cash flow.
   * @returns A promise that resolves to the created CashFlow.
   */
  static async createCashFlow(estateId: string, cashFlowData: Partial<CashFlow>): Promise<CashFlow> {
    try {
      const response = await api.post<CashFlow>(`/cashflows/${estateId}`, cashFlowData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating cash flow:', error);
      throw new Error(error.response?.data?.message || 'Failed to create cash flow.');
    }
  }

  /**
   * Update an existing cash flow.
   * @param estateId - UUID of the estate.
   * @param cashFlowId - ID of the cash flow to update.
   * @param updateData - Updated data for the cash flow.
   * @returns A promise that resolves to the updated CashFlow.
   */
  static async updateCashFlow(estateId: string, cashFlowId: string, updateData: Partial<CashFlow>): Promise<CashFlow> {
    try {
      const response = await api.patch<CashFlow>(`/cashflows/${estateId}/${cashFlowId}`, updateData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating cash flow:', error);
      throw new Error(error.response?.data?.message || 'Failed to update cash flow.');
    }
  }

  /**
   * Delete a cash flow.
   * @param estateId - UUID of the estate.
   * @param cashFlowId - ID of the cash flow to delete.
   * @returns A promise that resolves when the cash flow is deleted.
   */
  static async deleteCashFlow(estateId: string, cashFlowId: string): Promise<void> {
    try {
      await api.delete(`/cashflows/${estateId}/${cashFlowId}`);
    } catch (error: any) {
      console.error('Error deleting cash flow:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete cash flow.');
    }
  }

  
}


export const getRecentCashflows = async (
    estateId: string,
    count: number = 5
  ): Promise<CashFlow[]> => {
    const response = await api.get(`/cashflows/${estateId}/recent/${count}`);
    return response.data;
  };